import React, {FC, ReactNode} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Colors, Text, TransparentButton} from '@optimaxdev/design-system';

import classNames from 'classnames';
import {useSelector, useDispatch} from 'react-redux';

import {TYP_CONTACT_LENSES} from 'constants/analytics/events/typ';
import {CL_RX_METHOD} from 'constants/success';
import {getLabel, getPreviousMethod} from 'libs/success/helpers/helpers';
import {getPopupType, getTitle} from 'libs/success/prescriptionPopup/prescriptionPopup';
import {getPrescriptionStatusAfterUpdateOrder} from 'libs/success/statuses/statuses';
import {OrderDetailsItemsType} from 'types/orderDetailsTypes';

import {getDrawerState} from '../../../selectorsV2';
import {setDrawerMethod} from '../../../store/reducers/drawerPopup/drawerPopup';

import {getOrderError, getOrderItems} from '../../../store/selectors/orderStatus/orderStatus';

import s from './screenWrapper.scss';

export type PropsType = {
    children: ReactNode;
    buttons?: ReactNode;
};

const getError = (isError: boolean, rxMethod: string): ReactNode => {
    if (!isError) return '';
    switch (rxMethod) {
        case CL_RX_METHOD.EYE_EXAM_ELIGIBILITY:
            return 'An active vision test already exists for this email address.';
        case CL_RX_METHOD.EYE_EXAM:
            return (
                <>
                    There was an error processing your vision test link.
                    <br />
                    Please contact us or try again later.
                </>
            );
        case CL_RX_METHOD.UPLOAD:
            return 'There was an error processing your prescription. Please contact us or try again later.';
        default:
            return '';
    }
};

/**
 * Wrapper for the screens inside the drawer
 */
export const ScreenWrapper: FC<PropsType> = ({buttons, children}) => {
    const {item, rxMethod} = useSelector(getDrawerState);
    const items = useSelector(getOrderItems);
    const isError = useSelector(getOrderError);
    const error = getError(isError, rxMethod);

    const updatedItemStatus = getPrescriptionStatusAfterUpdateOrder(
        items,
        item as OrderDetailsItemsType,
    );

    const type = getPopupType(item, updatedItemStatus);
    const dispatch = useDispatch();
    const handleBack = () => {
        const method = getPreviousMethod(rxMethod);
        dispatch(
            setDrawerMethod({
                rxMethod: method,
                title: getTitle(method, type),
            }),
        );
        sendDesktopAnalyticsEvent(TYP_CONTACT_LENSES, `${getLabel(rxMethod, false, type)} - Back`);
    };

    return (
        <div className={s.container}>
            <div className={classNames(s.content, {[s.defaultScreen]: rxMethod === ''})}>
                {rxMethod && (
                    <div className={s.backButtonContainer}>
                        <TransparentButton
                            iconName="ButtonArrow"
                            onClick={handleBack}
                            size="small"
                            type="button"
                        >
                            Back
                        </TransparentButton>
                    </div>
                )}
                {children}
            </div>
            {buttons && (
                <div className={s.buttonsWrapper}>
                    <div className={s.buttons}>{buttons}</div>
                    {error && (
                        <Text
                            as="p"
                            color={Colors.burnt}
                            lineHeight="150%"
                            padding="8px 0 0 0"
                            size="13px"
                            textAlign="center"
                        >
                            {error}
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
};
